import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import authService from '../services/authService';
import capacityAssessmentService from '../services/capacityAssessmentService';
import BackToDashboard from '../components/BackToDashboard';

function CapacityAssessment() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_customers_per_month: '',
    additional_capacity: '',
    scale_speed: '',
    scaling_constraints: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const result = await authService.checkAuth();
    if (result.authenticated) {
      // Load existing data
      const dataResult = await capacityAssessmentService.getCapacityData();
      if (dataResult.success && dataResult.data) {
        setFormData({
          current_customers_per_month: dataResult.data.current_customers_per_month || '',
          additional_capacity: dataResult.data.additional_capacity || '',
          scale_speed: dataResult.data.scale_speed || '',
          scaling_constraints: dataResult.data.scaling_constraints || []
        });
      }
    } else {
      navigate('/login');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleConstraintChange = (constraint) => {
    const newConstraints = formData.scaling_constraints.includes(constraint)
      ? formData.scaling_constraints.filter(c => c !== constraint)
      : [...formData.scaling_constraints, constraint];

    setFormData({
      ...formData,
      scaling_constraints: newConstraints
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.current_customers_per_month || formData.current_customers_per_month < 0) {
      setError('Please enter current customers per month (use 0 if not launched)');
      return;
    }

    if (!formData.additional_capacity || formData.additional_capacity < 0) {
      setError('Please enter additional capacity');
      return;
    }

    if (!formData.scale_speed) {
      setError('Please select how quickly you can scale');
      return;
    }

    if (formData.scaling_constraints.length === 0) {
      setError('Please select at least one scaling constraint (or "None - can scale easily")');
      return;
    }

    setSaving(true);

    const result = await capacityAssessmentService.saveCapacityData(formData);

    setSaving(false);

    if (result.success) {
      setSuccess('Capacity assessment saved successfully!');
      setTimeout(() => {
        navigate('/dashboard/foundation');
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  const constraints = [
    { value: 'space', label: 'Physical space (office, warehouse, production floor)' },
    { value: 'equipment', label: 'Equipment/machinery (need to purchase/lease)' },
    { value: 'inventory', label: 'Inventory/supply chain (long lead times)' },
    { value: 'staffing', label: 'Staffing (hard to find qualified people)' },
    { value: 'personal_time', label: 'Your personal time (you\'re the bottleneck)' },
    { value: 'regulatory', label: 'Regulatory/licensing (legal constraints)' },
    { value: 'capital', label: 'Capital (need funding to scale)' },
    { value: 'quality', label: 'Quality control (hard to maintain standards at scale)' },
    { value: 'none', label: 'None - can scale easily' }
  ];

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <h2 className="mb-4">Capacity Assessment</h2>

              <Alert variant="info" className="mb-4">
                <strong>Before we plan your exit strategy,</strong> let's understand your growth capacity.
              </Alert>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4" controlId="current_customers_per_month">
                  <Form.Label><strong>How many customers/clients/orders are you currently serving per month?</strong></Form.Label>
                  <Form.Control
                    type="number"
                    name="current_customers_per_month"
                    value={formData.current_customers_per_month}
                    onChange={handleChange}
                    required
                    min="0"
                    placeholder="e.g., 25"
                  />
                  <Form.Text className="text-muted">
                    💡 Use "0" if you haven't launched yet.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="additional_capacity">
                  <Form.Label><strong>How many MORE could you comfortably handle right now without:</strong></Form.Label>
                  <ul className="mb-2">
                    <li>Hiring additional staff</li>
                    <li>Buying more equipment/inventory</li>
                    <li>Moving to larger space</li>
                    <li>Significantly reducing quality/service</li>
                  </ul>
                  <Form.Control
                    type="number"
                    name="additional_capacity"
                    value={formData.additional_capacity}
                    onChange={handleChange}
                    required
                    min="0"
                    placeholder="e.g., 50"
                  />
                  <Form.Text className="text-muted">
                    Additional customers/month you can handle with current setup
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="scale_speed">
                  <Form.Label><strong>If you reach capacity, how quickly can you scale up?</strong></Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="immediate"
                      label="Immediately (Digital/automated product, no physical constraints)"
                      checked={formData.scale_speed === 'immediate'}
                      onChange={handleChange}
                      id="scale-immediate"
                    />
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="1-2wk"
                      label="Within 1-2 weeks (Simple hiring, can onboard quickly)"
                      checked={formData.scale_speed === '1-2wk'}
                      onChange={handleChange}
                      id="scale-1-2wk"
                    />
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="1-2mo"
                      label="Within 1-2 months (Need to hire/train, moderate setup)"
                      checked={formData.scale_speed === '1-2mo'}
                      onChange={handleChange}
                      id="scale-1-2mo"
                    />
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="3-6mo"
                      label="Within 3-6 months (Significant hiring, equipment orders, setup time)"
                      checked={formData.scale_speed === '3-6mo'}
                      onChange={handleChange}
                      id="scale-3-6mo"
                    />
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="6+mo"
                      label="6+ months (Major infrastructure changes, factory retooling, etc.)"
                      checked={formData.scale_speed === '6+mo'}
                      onChange={handleChange}
                      id="scale-6mo"
                    />
                    <Form.Check
                      type="radio"
                      name="scale_speed"
                      value="difficult"
                      label="Very difficult to scale (Physical constraints, licensing, rare expertise)"
                      checked={formData.scale_speed === 'difficult'}
                      onChange={handleChange}
                      id="scale-difficult"
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-4" controlId="scaling_constraints">
                  <Form.Label><strong>What's your biggest constraint to scaling?</strong> (Select all that apply)</Form.Label>
                  <div className="mt-2">
                    {constraints.map((constraint) => (
                      <Form.Check
                        key={constraint.value}
                        type="checkbox"
                        label={constraint.label}
                        checked={formData.scaling_constraints.includes(constraint.value)}
                        onChange={() => handleConstraintChange(constraint.value)}
                        id={`constraint-${constraint.value}`}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Alert variant="info" className="mb-4">
                  <strong>💡 Why this matters:</strong>
                  <p className="mb-0 mt-2">
                    Your exit strategy needs to account for how quickly you can grow without sacrificing quality.
                    We'll build realistic growth targets that match your capacity.
                  </p>
                </Alert>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/foundation')}
                  >
                    Save for Later
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save and Continue'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CapacityAssessment;
